import React, { useState, useEffect, useMemo, useCallback } from "react";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../../common/components/loading/loading";
import { getMarketAttributes } from "../../../common/services/markets/marketAttributeService";
import { useTranslation } from "react-i18next";

const MarketAttributesTable = ({
  marketId,
  marketAttributes: propMarketAttributes,
  onRowClick,
}) => {
  const { t } = useTranslation(["markets"]);
  const [marketAttributes, setMarketAttributes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);

  // Map API data to view model
  const mapToViewModel = useCallback((attributes) => {
    return attributes.map((marketAttribute) => ({
      ...marketAttribute,
      // Ensure all required fields have values to prevent rendering issues
      priceBasisName: marketAttribute.priceBasisName || "",
      currencyName: marketAttribute.currencyName || "",
      uomName: marketAttribute.uomName || "",
      pricePrecision: marketAttribute.pricePrecision ?? 0,
      volumePrecision: marketAttribute.volumePrecision ?? 0,
    }));
  }, []);

  // Fetch market attributes on component mount if not provided as props
  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        if (!propMarketAttributes || propMarketAttributes.length === 0) {
          const data = await getMarketAttributes(marketId);
          setMarketAttributes(mapToViewModel(data));
        } else {
          setMarketAttributes(mapToViewModel(propMarketAttributes));
        }
      } catch (error) {
        console.error("Error fetching market attributes:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAttributes();
  }, [marketId, propMarketAttributes, mapToViewModel]);

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "priceBasisName",
        headerName: t("PriceBasis"),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "currencyName",
        headerName: t("Currency"),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "uomName",
        headerName: t("Uom"),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "pricePrecision",
        headerName: t("PricePrecision"),
        sortable: true,
        filter: true,
        flex: 1,
        type: "numericColumn",
      },
      {
        field: "volumePrecision",
        headerName: t("VolumePrecision"),
        sortable: true,
        filter: true,
        flex: 1,
        type: "numericColumn",
      },
    ],
    [t]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 100,
      sortable: true,
      filter: true,
    }),
    []
  );

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      // Enable pagination
      enabled: true,
      // Default page size
      pageSize: 10,
      // Available page size options for user to choose
      pageSizeOptions: [5, 10, 20, 50, 100],
    }),
    []
  );

  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
  }, []);

  if (isLoading) return <Loading />;

  return (
    /*<div className="marketAttributes-table">*/
    <ThemeableAgGrid
      className="table-bold clickable"
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      rowData={marketAttributes}
      onRowClicked={(params) => onRowClick && onRowClick(params.data)}
      getRowId={(params) =>
        params.data.marketAttributeId
          ? params.data.marketAttributeId.toString()
          : `${params.data.marketId}-${params.data.priceBasisName}`
      }
      animateRows={true}
      // Normal layout for pagination
      domLayout="autoHeight"
      // Enable pagination
      pagination={true}
      paginationPageSize={pageSize}
      paginationPageSizeSelector={paginationConfig.pageSizeOptions}
      onPaginationChanged={(event) => {
        const newPageSize = event.api.paginationGetPageSize();
        if (newPageSize !== pageSize) {
          handlePageSizeChange(newPageSize);
        }
      }}
      suppressPaginationPanel={false}
    />
    /*</div>*/
  );
};

export default MarketAttributesTable;
